import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Image,
  Typography,
  InputNumber,
  Divider,
  Col,
  Row,
  Statistic,
  Tag,
  Space,
  Tooltip,
} from "antd";
import { SafetyCertificateOutlined, InfoCircleOutlined } from "@ant-design/icons";
import "./ItemCard.css";

interface Item {
  Image: string[];
  Price: number;
  Name: string;
  Desc: string;
}

interface ProductCardProps {
  item: Item | null;
  onQuantityChange: (newQuantity: number, newTotal: number) => void;
}

const { Title, Paragraph, Text } = Typography;

const moneyUAH = (v: number) => `${Math.round(v).toLocaleString("uk-UA")} ₴`;

const ItemCard: React.FC<ProductCardProps> = ({ item, onQuantityChange }) => {
  const [qty, setQty] = useState<number>(1);

  useEffect(() => {
    setQty(1);
  }, [item?.Name]);

  const total = useMemo(() => {
    const price = item?.Price ?? 0;
    return qty * price;
  }, [qty, item?.Price]);

  const onChange = (value: number | null) => {
    const v = typeof value === "number" && value > 0 ? value : 1;
    setQty(v);
  };

  useEffect(() => {
    onQuantityChange(qty, total);
  }, [qty, total, onQuantityChange]);

  return (
    <Card hoverable className="pro-card" styles={{ body: { padding: 16 } }}
      cover={
        <div className="card-cover" style={{alignItems: 'center', display: 'flex', justifyContent: 'center', height: '100%', gap: 10}}>
          {item?.Image && item?.Image.length > 1 ?
              item.Image.map((img, i) => <Image
                  src={img}
                  alt={item?.Name}
                  height={265}
                  style={{ width: "100%", objectFit: "cover", borderRadius: 14 }}
                  preview={true}
              />)
              : ''
          }

        </div>
      }
    >
      <Space direction="vertical" size={10} style={{ width: "100%", height: "fit-content" }}>
        <Space wrap size={8}>
          <Tag icon={<SafetyCertificateOutlined />} color="green">
            Якісний виріб
          </Tag>
          <Tag color="blue">Офіційне оформлення</Tag>
          <Tooltip title="Опис можна розгорнути кнопкою “Детально”">
            <Tag icon={<InfoCircleOutlined />} color="gold">
              Інформація
            </Tag>
          </Tooltip>
        </Space>

        <div>
          <Title level={4} style={{ margin: 0, lineHeight: 1.2 }}>
            {item?.Name}
          </Title>

          <Paragraph
            style={{ marginTop: 8, marginBottom: 0 }}
            ellipsis={{ rows: 3, expandable: true, symbol: "Детально" }}
          >
            {item?.Desc}
          </Paragraph>
        </div>

        <div style={{ marginTop: 4 }}>
          <InputNumber
            addonBefore="Кількість"
            style={{ width: "100%" }}
            min={1}
            value={qty}
            onChange={onChange}
            changeOnWheel
          />
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 6 }}>
            Порада: колесиком миші можна швидко змінювати кількість
          </Text>
        </div>

        <Divider style={{ margin: "10px 0" }} />

        <Row gutter={16}>
          <Col span={12} style={{ display: "flex", justifyContent: "center" }}>
            <Statistic
              title={<Text type="secondary">Ціна за 1 од.</Text>}
              value={item?.Price ?? 0}
              formatter={(v) => moneyUAH(Number(v))}
            />
          </Col>
          <Col span={12} style={{ display: "flex", justifyContent: "center" }}>
            <Statistic
              title={<Text type="secondary">Загалом</Text>}
              value={total}
              formatter={(v) => moneyUAH(Number(v))}
              valueStyle={{ fontWeight: 800 }}
            />
          </Col>
        </Row>
      </Space>
    </Card>
  );
};

export default ItemCard;
